import React, {ChangeEvent, useEffect} from "react";
import {useNavigate, useParams} from "react-router-dom";

import DropDownMenu, {IChoice, findOptionFromList} from "@components/inputs/DropDownMenu";
import Label from "@components/inputs/text/Label";
import SubmitBtn from "@components/buttons/FormButtons/SubmitBtn";
import {NavBar} from "@components/navigation/NavBar/NavBar";
import Elliptical from "@components/spinners/Elliptical/Elliptical";
import InfoBox from "@components/cards/messageBox/InfoBox";
import useServiceHook from "@hooks/useServiceHook";
import {IPlan, PlanService} from "@services/SubscriptionPlans";
import {SubscriptionService} from "@services/index";
import {CouponListItem, SubscriptionDetails} from "@services/subscription/SubscriptionService";
import {Country} from "@utils/constants/localisation";
import {
    convertMajorToMinorCurrency,
    convertMinorToMajorCurrency,
    countrySpecificCurrency,
    getRootGroupFromAncestry
} from "@utils/misc/functions";
import breadcrumbObject from "../../SmartOpsHome/breadcrumbObject";

import {CurrentVehicleState} from "./currentVehicleDetailsReducer";
import {SelectedSubscriptionState} from "./selectedSubscriptionReducer";
import {PriceChangeAction, PriceChangeState, calculateNewWeeklyFee} from "./priceChangeReducer";

// Value used for the "no discount" choice. Empty so it reads as cleared in state.
const NO_COUPON_VALUE = "";

// This screen is the boundary between the major-currency endpoints and the minor-currency reducer.
// Rounding is required because multiplying a float by 100 does not land on a whole number
// (260.1 * 100 is 26010.000000000004 in JS).
const toMinor = (major: number): number => Math.round(convertMajorToMinorCurrency(major));
// Display only - the reducer and the exchange payload stay in minor currency.
const formatMinor = (minor: number, currency: string): string =>
    `${currency}${convertMinorToMajorCurrency(minor).toFixed(2)}`;

type Props = {
    navigateTarget: string;
    selectedSubscriptionState: SelectedSubscriptionState;
    currentVehicleState: CurrentVehicleState;
    priceChangeState: PriceChangeState;
    priceChangeDispatch: React.Dispatch<PriceChangeAction>;
};

export default function PriceChangePlan({
                                            navigateTarget,
                                            selectedSubscriptionState,
                                            currentVehicleState,
                                            priceChangeState,
                                            priceChangeDispatch,
                                        }: Props) {
    const {country} = useParams() as { country: Country };
    const navigate = useNavigate();

    const componentName = "Price change plan";
    const currency = countrySpecificCurrency(country);

    // In AU plans are per hub (NSW / VIC / QLD), taken from the current vehicle's group.
    // GB has a single plan state, matching the onboarding journey.
    const planState = country === Country.GB
        ? "LDN"
        : getRootGroupFromAncestry(currentVehicleState.vehicle.group_ancestry);

    // Only Flexi subscriptions reach this screen, so the current plan is the plan type to search.
    const planType = selectedSubscriptionState.selectedPlan;
    const allowPlanSearch = typeof planType === "string" && planType.length > 0;

    // AU customers move on to the per-vehicle Flexi Try plans from the pricing catalogue, which the
    // plans endpoint only returns when asked for v2. GB stays on the original plans.
    const planQuery = {
        plan_type: planType,
        state: planState,
        term: 0,
        ...(country === Country.AU && {catalog: "v2"}),
    };

    const [plansLoading, , , availablePlans] = useServiceHook<IPlan[]>(
        PlanService.getPlans,
        [planQuery, country],
        allowPlanSearch,
        [country]
    );

    const [couponsLoading, , , availableCoupons] = useServiceHook<CouponListItem[]>(
        SubscriptionService.getCoupons,
        [{country, "type[is]": "dynamic"}],
        true,
        [country]
    );

    // The subscription carries the fee and discount the customer is on today, for the comparison
    // shown on the next screen.
    const [subscriptionLoading, , , subscription] = useServiceHook<SubscriptionDetails>(
        SubscriptionService.getSubscriptionById,
        [selectedSubscriptionState.selectedSubsId, country],
        !!selectedSubscriptionState.selectedSubsId,
        [selectedSubscriptionState.selectedSubsId, country]
    );

    useEffect(() => {
        if (!subscription) return;
        priceChangeDispatch({
            type: "SUBSCRIPTION_PRICING_LOADED",
            payload: {
                // Zoho holds both of these in major currency
                oldWeeklyFee: subscription.weeklyRentalFee != null
                    ? toMinor(Number(subscription.weeklyRentalFee)) : null,
                oldWeeklyDiscount: subscription.totalWeeklyDiscount != null
                    ? toMinor(Number(subscription.totalWeeklyDiscount)) : null,
            },
        });
    }, [subscription]);

    const planOptions: IChoice[] = (availablePlans || []).map((plan: IPlan) => ({
        value: plan.id,
        label: plan.name,
        enabled: true,
    }));

    // Coupons are shown by the amount their ID declares, which is what the customer pays and what
    // the addendum has to say. Chargebee's own discountAmount is net of tax and would understate
    // every discount by the tax rate. A coupon whose ID declares nothing is left out entirely -
    // there is no figure that can honestly be put on a contract for it.
    // Sorted largest discount first. Chargebee returns them in no useful order, and the filter
    // above guarantees the value is a number, so the comparison needs nothing else. Safe to sort
    // in place because filter has already produced a new array - the hook's cached result is not
    // being reordered.
    const selectableCoupons: CouponListItem[] = (availableCoupons || [])
        .filter((coupon: CouponListItem) => coupon.weeklyDiscountInclTax != null)
        .sort((a, b) => Number(b.weeklyDiscountInclTax) - Number(a.weeklyDiscountInclTax));

    const couponOptions: IChoice[] = [
        {value: NO_COUPON_VALUE, label: "No discount", enabled: true},
        ...selectableCoupons.map((coupon: CouponListItem) => ({
            value: coupon.id,
            label: `${formatMinor(Number(coupon.weeklyDiscountInclTax), currency)} per week - ${coupon.id}`,
            enabled: true,
        })),
    ];

    // Zoho holds the discount amount rather than the coupon id, so the customer's existing coupon is
    // matched on value. Only preselects while the operator has not chosen one themselves.
    // Zoho's figure is tax inclusive (onboarding writes the same number the coupon id declares), so
    // it is compared against weeklyDiscountInclTax. Both are minor currency - no conversion here.
    useEffect(() => {
        if (!availableCoupons || priceChangeState.couponId) return;
        if (priceChangeState.oldWeeklyDiscount == null) return;
        const currentCoupon = selectableCoupons.find(
            (coupon: CouponListItem) => Number(coupon.weeklyDiscountInclTax) === priceChangeState.oldWeeklyDiscount
        );
        if (currentCoupon) {
            priceChangeDispatch({
                type: "USER_SELECTS_COUPON",
                payload: {couponId: currentCoupon.id, discount: Number(currentCoupon.weeklyDiscountInclTax)},
            });
        }
    }, [availableCoupons, priceChangeState.oldWeeklyDiscount]);

    const onSelectPlan = (event: ChangeEvent<HTMLSelectElement>) => {
        const selectedPlan = (availablePlans || []).find((plan: IPlan) => plan.id === event.target.value);
        if (!selectedPlan) return;
        priceChangeDispatch({
            type: "USER_SELECTS_PLAN",
            payload: {
                planId: selectedPlan.id,
                planName: selectedPlan.name,
                // Plan prices come from the plans endpoint in major currency
                weeklyFee: toMinor(selectedPlan.weeklyHireFee),
            },
        });
    };

    const onSelectCoupon = (event: ChangeEvent<HTMLSelectElement>) => {
        const couponId = event.target.value;
        // Searched among the selectable coupons only, so the discount subtracted from the plan fee
        // is always one the operator could actually see on screen.
        const selectedCoupon = selectableCoupons.find((coupon: CouponListItem) => coupon.id === couponId);
        priceChangeDispatch({
            type: "USER_SELECTS_COUPON",
            payload: {
                couponId,
                discount: selectedCoupon ? Number(selectedCoupon.weeklyDiscountInclTax) : null,
            },
        });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        navigate(navigateTarget);
    };

    const newWeeklyFee = calculateNewWeeklyFee(priceChangeState);
    // Nothing on this screen renders the customer's current fee, so there is no visible cue that
    // the subscription fetch is still in flight. Leaving before it settles unmounts this component
    // with the dispatch above never fired, and the old fee is then null on the review payload and
    // in the exchange record. `loading` settles to false on failure too, so a subscription Zoho has
    // no Rental_Fee for still lets the operator continue.
    const isSubmitDisabled = !priceChangeState.newPlanId || subscriptionLoading !== false;

    return (
        <>
            <NavBar pageTitle="New plan" backAction={undefined}/>
            <div style={{display: "flex", flexDirection: "column", margin: "2.5vh 0vw"}}></div>
            <InfoBox
                message={
                    <>
                        Select the Flexi plan and dynamic pricing discount the customer is moving on to.
                        <br/><br/>
                        The weekly price shown here is a preview and will be included in the signed addendum.
                        The actual price charged will depend upon the separate update to Chargebee.
                    </>
                }
            />
            <form style={{margin: "2.5vh 2.5vw"}} onSubmit={handleSubmit}>
                <div style={{display: "flex", flexDirection: "column", margin: "2.5vh 0vw"}}>
                    <Label text="New plan:" requiredFieldInd={true}/>
                    {plansLoading === false || plansLoading === null
                        ? <DropDownMenu
                            menuName="newPlan"
                            defaultVal={findOptionFromList(priceChangeState.newPlanId, planOptions)}
                            required
                            choices={planOptions}
                            onSelect={onSelectPlan}
                            testId="plan-dropdown"
                        />
                        : <LoadingSpinner/>
                    }
                </div>

                <div style={{display: "flex", flexDirection: "column", margin: "2.5vh 0vw"}}>
                    <Label text="Dynamic pricing discount:"/>
                    {couponsLoading === false || couponsLoading === null
                        ? <DropDownMenu
                            menuName="coupon"
                            defaultVal={findOptionFromList(priceChangeState.couponId, couponOptions)}
                            required={false}
                            choices={couponOptions}
                            onSelect={onSelectCoupon}
                            key={priceChangeState.couponId}
                            testId="coupon-dropdown"
                        />
                        : <LoadingSpinner/>
                    }
                </div>

                {newWeeklyFee != null && (
                    <p style={{margin: "2.5vh 0vw"}} data-testid="new-weekly-fee-preview">
                        New weekly fee: <b>{formatMinor(newWeeklyFee, currency)}</b>
                        {priceChangeState.couponDiscount && priceChangeState.newPlanWeeklyFee != null
                            ? ` (${formatMinor(priceChangeState.newPlanWeeklyFee, currency)} less a ` +
                              `${formatMinor(priceChangeState.couponDiscount, currency)} discount)`
                            : ""}
                    </p>
                )}

                <SubmitBtn
                    text="Next"
                    breadcrumbObject={breadcrumbObject}
                    componentName={componentName}
                    disabled={isSubmitDisabled}
                />
            </form>
        </>
    );
}

const LoadingSpinner = () => (
    <div style={{height: "5vh"}}>
        <Elliptical/>
    </div>
);
